// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { classNames } from '@shared/ui-kit/utils';
import { isNullOrUndefined } from '@shared/utils';
import { type FC, useMemo } from 'react';

export type CreditLineUsageTier = 'low' | 'medium' | 'high';

export const getCreditLineUsageTier = (ratio: number): CreditLineUsageTier => {
  if (ratio >= 0.9) {
    return 'high';
  }

  if (ratio >= 0.8) {
    return 'medium';
  }

  return 'low';
};

export interface CreditLineUsageProgressProps {
  readonly ratio: number | null | undefined;
}

export const CreditLineUsageProgress: FC<CreditLineUsageProgressProps> = ({
  ratio,
}) => {
  const usagePercent = useMemo(() => {
    if (isNullOrUndefined(ratio) || Number.isNaN(ratio)) {
      return 0;
    }

    return Math.min(Math.max(Math.round(ratio * 100), 0), 100);
  }, [ratio]);

  const tier = useMemo(() => getCreditLineUsageTier(ratio ?? 0), [ratio]);

  return (
    <progress
      value={usagePercent}
      max={100}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={usagePercent}
      aria-valuetext={`${usagePercent}%`}
      aria-label={`${usagePercent}% usage, ${tier}`}
      data-usage-tier={tier}
      data-testid="credit-line-usage-progress"
      className={classNames(
        'h-2 w-full min-w-[4rem] flex-1 appearance-none border-0 bg-transparent',
        '[&::-webkit-progress-bar]:rounded-full [&::-webkit-progress-bar]:bg-border-sep',
        'data-[usage-tier=low]:[&::-webkit-progress-value]:rounded-full data-[usage-tier=low]:[&::-webkit-progress-value]:bg-positive',
        'data-[usage-tier=medium]:[&::-webkit-progress-value]:rounded-full data-[usage-tier=medium]:[&::-webkit-progress-value]:bg-warning',
        'data-[usage-tier=high]:[&::-webkit-progress-value]:rounded-full data-[usage-tier=high]:[&::-webkit-progress-value]:bg-negative',
      )}
    />
  );
};
